/* eslint-disable testing-library/no-debugging-utils */
import { type QueryClient } from '@tanstack/react-query'
import { Logger } from '@egw/logger'

const log = new Logger('Query Client')
/**
 * Transfer invalidation configuration types
 */
export interface TransferInvalidationConfig {
  /** Query client instance */
  client: QueryClient
  /** Refetch type for invalidated queries */
  refetchType?: 'active' | 'all' | 'inactive'
}

export interface AchTransferInvalidationConfig extends TransferInvalidationConfig {
  /** Bank account alias affected by ACH transfer */
  bankAccountAlias: number
}

export interface BankInvestmentTransferInvalidationConfig extends TransferInvalidationConfig {
  /** Bank account alias affected by transfer */
  bankAccountAlias: number
  /** Investment account alias affected by transfer */
  investmentAccountAlias: number
}

export interface InternalBankTransferInvalidationConfig extends TransferInvalidationConfig {
  /** From bank account alias */
  fromBankAccountAlias: number
  /** To bank account alias */
  toBankAccountAlias: number
}

export interface BankAccountNameUpdateInvalidationConfig extends TransferInvalidationConfig {
  /** Bank account alias affected by name update */
  bankAccountAlias: number
}

export interface InvestmentAccountNameUpdateInvalidationConfig extends TransferInvalidationConfig {
  /** Investment account alias affected by name update */
  investmentAccountAlias: number
}

/**
 * Query key matcher utility functions
 */
const queryKeyMatchers = {
  /** Matches wealth overview queries */
  isWealthOverview: (queryKey: readonly unknown[]): boolean => {
    const [type] = queryKey
    return type === 'wealthOverview'
  },

  /** Matches bank account detail queries for specific alias */
  isBankAccountDetail: (queryKey: readonly unknown[], alias: number): boolean => {
    const [type, params] = queryKey
    return type === 'bankAccountDetail' && (params as { alias: number }).alias === alias
  },

  /** Matches bank account activity queries for specific alias */
  isBankAccountActivity: (queryKey: readonly unknown[], alias: number): boolean => {
    const [type, params] = queryKey
    return type === 'bankAccountActivity' && (params as { alias: number }).alias === alias
  },

  /** Matches investment account detail queries for specific alias */
  isInvestmentAccountDetail: (queryKey: readonly unknown[], alias: number): boolean => {
    const [type, aliasParam] = queryKey
    return type === 'getInvestmentAccountDetail' && aliasParam === alias
  },

  /** Matches investment account activity queries for specific alias */
  isInvestmentAccountActivity: (queryKey: readonly unknown[], alias: number): boolean => {
    const [type, params] = queryKey
    return type === 'getInvestmentAccountActivity' && (params as { alias: number }).alias === alias
  },

  /** Matches portfolio overview asset allocation queries */
  isPortfolioOverviewAssetAllocation: (queryKey: readonly unknown[]): boolean => {
    const [type] = queryKey
    return type === 'portfolioOverviewAssetAllocation'
  },
}

/**
 * Invalidates queries after ACH transfer completion
 *
 * Invalidates:
 * - WealthOverview
 * - bankAccountDetail for the specified alias
 * - bankAccountActivity for the specified alias
 */
export const invalidateAchTransferQueries = ({
  client,
  bankAccountAlias,
  refetchType = 'all',
}: AchTransferInvalidationConfig): void => {
  log.debug('invalidateAchTransferQueries invoked', {
    method: 'invalidateAchTransferQueries',
    bankAccountAlias,
    refetchType,
  })
  void client.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query

      // Invalidate wealth overview
      if (queryKeyMatchers.isWealthOverview(queryKey)) {
        return true
      }

      // Invalidate bank account detail for the affected alias
      if (queryKeyMatchers.isBankAccountDetail(queryKey, bankAccountAlias)) {
        return true
      }

      // Invalidate bank account activity for the affected alias
      if (queryKeyMatchers.isBankAccountActivity(queryKey, bankAccountAlias)) {
        return true
      }

      return false
    },
    refetchType,
  })
}

/**
 * Invalidates queries after bank-to-investment transfer completion
 *
 * Invalidates:
 * - WealthOverview
 * - bankAccountDetail for the bank account alias
 * - bankAccountActivity for the bank account alias
 * - investmentAccountDetail for the investment account alias
 * - investmentAccountActivity for the investment account alias
 * - portfolioOverviewAssetAllocation
 */
export const invalidateBankInvestmentTransferQueries = ({
  client,
  bankAccountAlias,
  investmentAccountAlias,
  refetchType = 'all',
}: BankInvestmentTransferInvalidationConfig): void => {
  log.debug('invalidateBankInvestmentTransferQueries invoked', {
    method: 'invalidateBankInvestmentTransferQueries',
    bankAccountAlias,
    investmentAccountAlias,
    refetchType,
  })
  void client.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query

      // Invalidate wealth overview
      if (queryKeyMatchers.isWealthOverview(queryKey)) {
        return true
      }

      // Invalidate bank account detail for the affected alias
      if (queryKeyMatchers.isBankAccountDetail(queryKey, bankAccountAlias)) {
        return true
      }

      // Invalidate bank account activity for the affected alias
      if (queryKeyMatchers.isBankAccountActivity(queryKey, bankAccountAlias)) {
        return true
      }

      // Invalidate investment account detail for the affected alias
      if (queryKeyMatchers.isInvestmentAccountDetail(queryKey, investmentAccountAlias)) {
        return true
      }

      // Invalidate investment account activity for the affected alias
      if (queryKeyMatchers.isInvestmentAccountActivity(queryKey, investmentAccountAlias)) {
        return true
      }

      // Invalidate portfolio overview asset allocation
      if (queryKeyMatchers.isPortfolioOverviewAssetAllocation(queryKey)) {
        return true
      }

      return false
    },
    refetchType,
  })
}

/**
 * Invalidates queries after internal bank transfer completion
 *
 * Invalidates:
 * - WealthOverview
 * - bankAccountDetail for BOTH bank account aliases
 * - bankAccountActivity for BOTH bank account aliases
 */
export const invalidateInternalBankTransferQueries = ({
  client,
  fromBankAccountAlias,
  toBankAccountAlias,
  refetchType = 'all',
}: InternalBankTransferInvalidationConfig): void => {
  log.debug('invalidateInternalBankTransferQueries invoked', {
    method: 'invalidateInternalBankTransferQueries',
    fromBankAccountAlias,
    toBankAccountAlias,
    refetchType,
  })
  void client.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query

      // Invalidate wealth overview
      if (queryKeyMatchers.isWealthOverview(queryKey)) {
        return true
      }

      // Invalidate bank account detail for both aliases
      if (
        queryKeyMatchers.isBankAccountDetail(queryKey, fromBankAccountAlias) ||
        queryKeyMatchers.isBankAccountDetail(queryKey, toBankAccountAlias)
      ) {
        return true
      }

      // Invalidate bank account activity for both aliases
      if (
        queryKeyMatchers.isBankAccountActivity(queryKey, fromBankAccountAlias) ||
        queryKeyMatchers.isBankAccountActivity(queryKey, toBankAccountAlias)
      ) {
        return true
      }

      return false
    },
    refetchType,
  })
}

/**
 * Invalidates queries after bank account name update
 *
 * Invalidates:
 * - WealthOverview
 * - bankAccountDetail for the specified alias
 * - bankAccountActivity for the specified alias
 */
export const invalidateBankAccountNameUpdateQueries = ({
  client,
  bankAccountAlias,
  refetchType = 'all',
}: BankAccountNameUpdateInvalidationConfig): void => {
  log.debug('invalidateBankAccountNameUpdateQueries invoked', {
    method: 'invalidateBankAccountNameUpdateQueries',
    bankAccountAlias,
    refetchType,
  })
  void client.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query

      // Invalidate wealth overview
      if (queryKeyMatchers.isWealthOverview(queryKey)) {
        return true
      }

      // Invalidate bank account detail for the specified alias
      if (queryKeyMatchers.isBankAccountDetail(queryKey, bankAccountAlias)) {
        return true
      }

      return false
    },
    refetchType,
  })
}

/**
 * Invalidates queries after investment account name update
 *
 * Invalidates:
 * - WealthOverview
 * - InvestmentAccountDetail for the specified alias
 * - InvestmentAccountActivity for the specified alias
 */
export const invalidateInvestmentAccountUpdateQueries = ({
  client,
  investmentAccountAlias,
  refetchType = 'all',
}: InvestmentAccountNameUpdateInvalidationConfig): void => {
  log.debug('invalidateInvestmentAccountUpdateQueries invoked', {
    method: 'invalidateInvestmentAccountUpdateQueries',
    investmentAccountAlias,
    refetchType,
  })
  void client.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query

      // Invalidate wealth overview
      if (queryKeyMatchers.isWealthOverview(queryKey)) {
        return true
      }

      // Invalidate investment account detail for the specified alias
      if (queryKeyMatchers.isInvestmentAccountDetail(queryKey, investmentAccountAlias)) {
        return true
      }

      return false
    },
    refetchType,
  })
}

/**
 * Generic transfer query invalidation utility
 * Useful for custom invalidation scenarios or debugging
 */
export const invalidateTransferQueries = ({
  client,
  bankAccountAliases = [],
  investmentAccountAliases = [],
  includeWealthOverview = true,
  refetchType = 'all',
}: {
  client: QueryClient
  bankAccountAliases?: number[]
  investmentAccountAliases?: number[]
  includeWealthOverview?: boolean
  refetchType?: 'active' | 'all' | 'inactive'
}): void => {
  log.debug('invalidateTransferQueries invoked', {
    method: 'invalidateTransferQueries',
    bankAccountAliases,
    investmentAccountAliases,
    includeWealthOverview,
    refetchType,
  })
  void client.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query

      // Invalidate wealth overview if requested
      if (includeWealthOverview && queryKeyMatchers.isWealthOverview(queryKey)) {
        return true
      }

      // Invalidate bank account queries for specified aliases
      for (const alias of bankAccountAliases) {
        if (
          queryKeyMatchers.isBankAccountDetail(queryKey, alias) ||
          queryKeyMatchers.isBankAccountActivity(queryKey, alias)
        ) {
          return true
        }
      }

      // Invalidate investment account queries for specified aliases
      for (const alias of investmentAccountAliases) {
        if (
          queryKeyMatchers.isInvestmentAccountDetail(queryKey, alias) ||
          queryKeyMatchers.isInvestmentAccountActivity(queryKey, alias)
        ) {
          return true
        }
      }

      return false
    },
    refetchType,
  })
}

/**
 * Utility to log which queries would be invalidated (for debugging)
 */
export const debugTransferQueryInvalidation = ({
  client,
  bankAccountAliases = [],
  investmentAccountAliases = [],
  includeWealthOverview = true,
}: {
  client: QueryClient
  bankAccountAliases?: number[]
  investmentAccountAliases?: number[]
  includeWealthOverview?: boolean
}): void => {
  log.debug('debugTransferQueryInvalidation invoked', {
    method: 'debugTransferQueryInvalidation',
    bankAccountAliases,
    investmentAccountAliases,
    includeWealthOverview,
  })
  const queryCache = client.getQueryCache()
  const allQueries = queryCache.getAll()

  const queriesToInvalidate = allQueries.filter((query) => {
    const { queryKey } = query

    if (includeWealthOverview && queryKeyMatchers.isWealthOverview(queryKey)) {
      return true
    }

    for (const alias of bankAccountAliases) {
      if (
        queryKeyMatchers.isBankAccountDetail(queryKey, alias) ||
        queryKeyMatchers.isBankAccountActivity(queryKey, alias)
      ) {
        return true
      }
    }

    for (const alias of investmentAccountAliases) {
      if (
        queryKeyMatchers.isInvestmentAccountDetail(queryKey, alias) ||
        queryKeyMatchers.isInvestmentAccountActivity(queryKey, alias)
      ) {
        return true
      }
    }

    return false
  })

  // Log detailed query information if enabled
  log.debug(
    'Detailed queries to invalidate:',
    queriesToInvalidate.map((q) => ({
      queryKey: q.queryKey,
      state: q.state.status,
      dataUpdatedAt: q.state.dataUpdatedAt,
    })),
  )
}
